import { useMaquetesTheme } from '../context/MaquetesThemeContext';
import { OrcamentoIcon } from '../components/OrcamentoUI';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

/* ============================================================
   RECONCILIAÇÃO DE EXTRATOS
   Motor de reconciliação (extratos BCP/Revolut + faturas, com
   picagem dos vistos no PDF) servido pelo serviço interno, dentro
   da aplicação e protegido pelo login do SPROD Gestão.

   Quando embutido, a navegação é a do gest: o menu próprio da app
   é escondido (na 1.ª carga pelo parâmetro ?embutido=1 e, nas
   navegações seguintes, por CSS injetado — o iframe é da mesma
   origem, o que o permite).
   ============================================================ */
const Reconciliador = ({ embutido = false, rota = '/', nonce = 0 }) => {
    const navigate = useNavigate();
    const { theme, active } = useMaquetesTheme();
    const [estado, setEstado] = useState('a-preparar'); // a-preparar | pronto | erro
    const [erro, setErro] = useState('');
    const frameRef = useRef(null);

    useEffect(() => {
        // Cria o cookie de sessão para o iframe poder autenticar-se
        api.post('/reconciliador/sessao')
            .then(() => setEstado('pronto'))
            .catch(e => { setEstado('erro'); setErro(e.response?.data?.error || e.message); });
    }, []);

    // Esconder o cabeçalho/menu próprios da app em cada navegação interna
    const aoCarregar = useCallback(() => {
        try {
            const doc = frameRef.current?.contentDocument;
            if (!doc) return;
            if (embutido && !doc.getElementById('gest-embed-style')) {
                const s = doc.createElement('style');
                s.id = 'gest-embed-style';
                s.textContent = '.apptop,.mainnav{display:none!important}.wrap{padding-top:8px!important}';
                doc.head.appendChild(s);
            }
            let liquidStyle = doc.getElementById('gest-liquid-style');
            if (theme === 'liquido' && active) {
                if (!liquidStyle) { liquidStyle = doc.createElement('style'); liquidStyle.id = 'gest-liquid-style'; doc.head.appendChild(liquidStyle); }
                liquidStyle.textContent = `
                    :root { --ink:#294b69; --ink-soft:#6d87a0; --line:#c5dbe680; --line-strong:#b6cee4; --bg:transparent; --panel:#f6faffb8; --brand:#397bb2; --brand-dark:#28649c; --brand-soft:#dcebf9; --accent:#397bb2; --accent-dark:#28649c; --teal:#397bb2; --teal-soft:#dcebf9; --shadow:inset 0 1px #ffffff80; --radius:16px; --radius-sm:10px; }
                    body { font-family:${getComputedStyle(document.body).fontFamily}; background:transparent; color:#294b69; font-size:13px; }
                    input:not([type=checkbox]):not([type=radio]):not([type=file]),select,textarea { font-family:inherit; font-size:14px; font-weight:400; color:#294d61; border:1px solid #a4bfd780; border-radius:10px; background:#ffffffbf; }
                    input[type=checkbox] { accent-color:#397bb2; }
                    button,.btn { font-family:inherit; border-radius:10px; font-size:12px; }
                    table { font-family:inherit; background:#fff; line-height:1.4; }
                    table th { font-size:10px; font-weight:600; color:#59738d; background:#edf4fb; padding:10px; }
                    table td { font-size:12px; font-weight:400; color:#294b69; background:#fff; padding:9px 10px; }
                    table tbody tr:hover td { background:#f1f8fb; }
                    table td strong { font-weight:400; }
                    .card,.panel { border:1px solid #ffffffba; background:#f6faffb8; border-radius:18px; }
                    .badge,.pill { font-size:10px; font-weight:600; border-radius:8px; }
                `;
            } else liquidStyle?.remove();
        } catch { /* origem diferente: ignora */ }
    }, [embutido, theme, active]);
    useEffect(() => { aoCarregar(); }, [aoCarregar]);

    const src = `/api/reconciliador${rota}${rota.includes('?') ? '&' : '?'}embutido=1`;

    // Carregar no separador que já está ativo tem de trazer o iframe de volta ao
    // início dessa secção. Sem isto não acontecia nada: o React vê o mesmo `rota`
    // e não mexe no src, enquanto o iframe já navegou para dentro (a ficha de um
    // cartão, por exemplo). O `nonce` muda a cada clique e força o regresso.
    const primeiraVez = useRef(true);
    useEffect(() => {
        if (primeiraVez.current) { primeiraVez.current = false; return; }
        if (frameRef.current) frameRef.current.src = src;
    }, [src, nonce]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: embutido ? 'calc(100vh - 230px)' : 'calc(100vh - 20px)', minHeight: 540 }}>
            {!embutido && (
                <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '16px 24px 10px' }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.3rem' }}><OrcamentoIcon name="chart" fallback="🧮" /> Reconciliação de Extratos</h1>
                        <p style={{ margin: '3px 0 0', color: '#64748b', fontSize: '0.82rem' }}>
                            Extratos do banco cruzados com as faturas · vistos picados no PDF para o contabilista
                        </p>
                    </div>
                    <div data-liquid-actions="true" style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => navigate('/despesas')} style={btnSec}>← Despesas</button>
                        <button onClick={() => { if (frameRef.current) frameRef.current.src = frameRef.current.src; }} style={btnSec}><OrcamentoIcon name="refresh" fallback="🔄" /> Atualizar</button>
                    </div>
                </div>
            )}

            {estado === 'a-preparar' && <p style={{ padding: embutido ? 0 : '0 24px', color: '#718096' }}>A preparar…</p>}
            {estado === 'erro' && (
                <div style={{ margin: embutido ? 0 : '0 24px', background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 10, padding: '14px 16px' }}>
                    <OrcamentoIcon name="warning" fallback="⚠️" /> Não foi possível iniciar a sessão do reconciliador: {erro}
                </div>
            )}
            {estado === 'pronto' && (
                <iframe
                    ref={frameRef}
                    onLoad={aoCarregar}
                    title="Reconciliação de Extratos"
                    src={src}
                    style={{
                        flex: 1, width: '100%', minHeight: 540, background: '#fff',
                        border: '1px solid #e2e8f0', borderRadius: 10,
                    }}
                />
            )}
        </div>
    );
};

const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };

export default Reconciliador;
